import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { setDoc } from "firebase/firestore";
import { db, storage } from "../firebase";
import { useAuth } from "./AuthProvider";
import { useVenue } from "./VenueProvider";
import MetodoPagoField from "./shared/MetodoPagoField";
import ComprobanteFooter from "./shared/ComprobanteFooter";
import DocumentUploader from "./shared/DocumentUploader";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { fmtMXNInput, normalizeMoneyInput } from "../utils/money";
import { showAlert } from "../alertService";
import { Badge } from "./badge";
import { Button } from "./button";
import DialogoConfirmacion from "./shared/DialogoConfirmacion";
import ResolverDepositoDialog from "./shared/ResolverDepositoDialog";
import { resolucionDeposito } from "../utils/depositoEstado";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  BanknotesIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const fmtMoney = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "$0";
  return "$" + Math.abs(n).toLocaleString("es-MX", {
    minimumFractionDigits: n % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (ts) => {
  if (!ts) return "-";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "-";
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
};

const ESTADO_BADGE = {
  activo: { label: "En custodia", color: "amber" },
  devuelto: { label: "Devuelto", color: "emerald" },
  aplicado: { label: "Aplicado", color: "red" },
};

const dateToInput = (ts) => {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const inputToTimestamp = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return Timestamp.fromDate(new Date(y, m - 1, d));
};

const DepositoForm = ({ initial, mode, onCancel, onSubmit, venueId, eventId }) => {
  const { metodosPagoConDefaults: metodosPago } = useVenue();
  const [monto, setMonto] = useState(initial?.monto ? String(initial.monto) : "");
  const [fecha, setFecha] = useState(dateToInput(initial?.fecha) || dateToInput(new Date()));
  const [metodo, setMetodo] = useState(initial?.metodo || "");
  const [notas, setNotas] = useState(initial?.notas || "");
  const [pendingArchivo, setPendingArchivo] = useState(null);

  const isCreate = mode !== "edit";
  const storagePrefix = isCreate && venueId && eventId
    ? `venues/${venueId}/eventos/${eventId}/archivos`
    : null;

  const cleanupPendingBlob = async () => {
    if (pendingArchivo?.storagePath) {
      try { await deleteObject(storageRef(storage, pendingArchivo.storagePath)); } catch (_) {}
    }
  };

  const handleArchivoUploaded = async (payload) => {
    await cleanupPendingBlob();
    setPendingArchivo(payload);
  };

  const handleRemoveArchivo = async () => {
    await cleanupPendingBlob();
    setPendingArchivo(null);
  };

  const handleCancel = async () => {
    await cleanupPendingBlob();
    onCancel();
  };

  const handleSubmit = () => {
    const m = Number(monto);
    if (!m || m <= 0) {
      showAlert("Ingresa un monto válido.", "error");
      return;
    }
    onSubmit({
      monto: m,
      fecha: inputToTimestamp(fecha) || serverTimestamp(),
      metodo: metodo.trim(),
      notas: notas.trim(),
      archivo: isCreate ? pendingArchivo : null,
    });
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Monto (MXN)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
            <input
              type="text"
              inputMode="decimal"
              pattern="-?[0-9.,]*"
              value={monto ? fmtMXNInput(monto) : ""}
              onChange={(e) => setMonto(normalizeMoneyInput(e.target.value))}
              placeholder="0"
              className="w-full rounded-lg border border-zinc-300 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Fecha</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Método</label>
        <MetodoPagoField
          value={metodo}
          onChange={setMetodo}
          metodosPago={metodosPago}
          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          placeholder="Transferencia, efectivo, etc."
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Notas</label>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          rows={2}
          placeholder="Referencia, contrato, etc."
        />
      </div>
      {isCreate && storagePrefix && (
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            Comprobante (opcional)
          </label>
          {pendingArchivo ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-zinc-800">{pendingArchivo.filename}</div>
                <div className="text-xs text-zinc-400">Adjunto al registrar</div>
              </div>
              <button
                type="button"
                onClick={handleRemoveArchivo}
                className="text-xs text-zinc-500 hover:text-red-600"
              >
                Quitar
              </button>
            </div>
          ) : (
            <DocumentUploader
              storagePathPrefix={storagePrefix}
              buttonLabel="Adjuntar comprobante"
              withDownloadUrl={false}
              onUploaded={handleArchivoUploaded}
            />
          )}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <Button type="button" outline onClick={handleCancel}>Cancelar</Button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
          style={{ backgroundColor: ACCENT }}
        >
          {mode === "edit" ? "Guardar" : "Registrar"}
        </button>
      </div>
    </div>
  );
};

// `soloLectura` (inversionista, P-FRA-13): la lista con montos, estado y
// comprobantes, sin registrar, resolver, reactivar, editar ni borrar.
const EventClienteDepositos = ({ venueId, eventId, eventNombre, soloLectura = false }) => {
  const { userInfo, user } = useAuth();
  const { loadEventArchivos } = useVenue();
  const [depositos, setDepositos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Resolver (aplicar / devolver / repartir) y reactivar viven en diálogos
  // compartidos; aquí solo se guarda qué depósito está en juego.
  const [resolviendo, setResolviendo] = useState(null);
  const [savingResolver, setSavingResolver] = useState(false);
  const [reactivando, setReactivando] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [archivos, setArchivos] = useState([]);

  const reloadArchivos = useCallback(async () => {
    if (!venueId || !eventId || !loadEventArchivos) {
      setArchivos([]);
      return;
    }
    try {
      const items = await loadEventArchivos("default", eventId);
      setArchivos(items || []);
    } catch (err) {
      console.error("Error loading deposito archivos:", err);
      setArchivos([]);
    }
  }, [venueId, eventId, loadEventArchivos]);

  useEffect(() => { reloadArchivos(); }, [reloadArchivos]);

  const archivosByDeposito = useMemo(() => {
    const idx = {};
    for (const a of archivos) {
      if (a?.linkedTo?.kind === "deposito" && a?.linkedTo?.id) {
        const id = a.linkedTo.id;
        const role = a.linkedRole === "devolucion" ? "devolucion" : "pago";
        if (!idx[id]) idx[id] = {};
        if (!idx[id][role]) idx[id][role] = a;
      }
    }
    return idx;
  }, [archivos]);

  // Subscribe scoped to this event so the panel works even when the user is
  // viewing an event whose venue isn't the currently selected venue.
  useEffect(() => {
    if (!venueId || !eventId) {
      setDepositos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, "venues", venueId, "clienteDepositos"),
      where("eventId", "==", eventId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const ta = a.fecha?.toMillis?.() || 0;
          const tb = b.fecha?.toMillis?.() || 0;
          return tb - ta;
        });
        setDepositos(rows);
        setLoading(false);
      },
      (err) => {
        console.error("EventClienteDepositos snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId, eventId]);

  const totals = useMemo(() => {
    const t = { activo: 0, devuelto: 0, aplicado: 0 };
    depositos.forEach((d) => {
      const monto = Number(d.monto) || 0;
      if (d.estado === "activo") {
        t.activo += monto;
      } else if (d.estado === "devuelto") {
        // Parciales: la porción devuelta cuenta en devuelto, el resto en aplicado.
        const devuelto = Number(d.montoDevuelto ?? d.monto) || 0;
        t.devuelto += devuelto;
        t.aplicado += Math.max(0, monto - devuelto);
      } else if (d.estado === "aplicado") {
        t.aplicado += monto;
      }
    });
    return t;
  }, [depositos]);

  const logAction = (action, depositoId, details = {}) => {
    logBitacora(
      venueId,
      {
        action,
        entity: "evento_deposito",
        entityId: depositoId,
        entityName: eventNombre || "",
        details: { eventId, ...details },
      },
      userInfo,
      user
    );
  };

  const handleCreate = async (data) => {
    if (!venueId || !eventId) return;
    try {
      const col = collection(db, "venues", venueId, "clienteDepositos");
      const payload = {
        eventId,
        monto: data.monto,
        fecha: data.fecha,
        estado: "activo",
        metodo: data.metodo || "",
        notas: data.notas || "",
        montoDevuelto: null,
        fechaDevolucion: null,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      };
      const ref = await addDoc(col, payload);
      logAction(BITACORA_ACTIONS.EVENTO_DEPOSITO_CREATE, ref.id, {
        monto: data.monto,
      });

      if (data.archivo) {
        const label = `Comprobante de depósito ${fmtDate(data.fecha)}`.trim();
        const archivoRef = doc(
          collection(db, "venues", venueId, "calendars", "default", "events", eventId, "archivos")
        );
        try {
          await setDoc(archivoRef, {
            label,
            tipo: "comprobante",
            storagePath: data.archivo.storagePath,
            filename: data.archivo.filename,
            fileSize: data.archivo.fileSize,
            mimeType: data.archivo.mimeType,
            uploadedAt: Timestamp.now(),
            uploadedBy: user?.uid || null,
            uploadedByName: userInfo?.name || "",
            linkedTo: { kind: "deposito", id: ref.id },
            linkedRole: "pago",
          });
          logAction(BITACORA_ACTIONS.DEPOSITO_COMPROBANTE_ATTACH, ref.id, {
            label,
            filename: data.archivo.filename,
            fileSize: data.archivo.fileSize,
          });
          reloadArchivos();
        } catch (archivoErr) {
          console.error("Error saving comprobante doc:", archivoErr);
          showAlert("Depósito creado, pero falló al adjuntar el comprobante.", "warning");
          try { await deleteObject(storageRef(storage, data.archivo.storagePath)); } catch (_) {}
        }
      }

      setShowAdd(false);
      showAlert("Depósito registrado", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const handlePatch = async (id, patch, action, bitacoraDetails = {}) => {
    if (!venueId) return;
    try {
      const ref = doc(db, "venues", venueId, "clienteDepositos", id);
      await updateDoc(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logAction(action, id, bitacoraDetails);
      return true;
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
      return false;
    }
  };

  // Desenlace del depósito desde el diálogo compartido. Misma forma de doc
  // que antes (estado + montoDevuelto + fechaDevolucion) para que el ledger,
  // Cuentas y las chips sigan leyendo igual; el comprobante de devolución
  // se cuelga del evento con linkedRole "devolucion", como siempre.
  const handleResolver = async (res) => {
    const d = resolviendo;
    if (!d) return;
    setSavingResolver(true);
    try {
      const r = resolucionDeposito(d.monto, res.montoAplicado);
      const fechaTs = inputToTimestamp(res.fechaStr) || serverTimestamp();
      const hayDevolucion = (r.montoDevuelto || 0) > 0;
      const ok = await handlePatch(
        d.id,
        {
          estado: r.estado,
          montoDevuelto: r.montoDevuelto,
          fechaDevolucion: fechaTs,
          metodoDevolucion: hayDevolucion ? res.metodo || null : null,
          notasDevolucion: res.notas || null,
        },
        hayDevolucion
          ? BITACORA_ACTIONS.EVENTO_DEPOSITO_DEVOLUCION
          : BITACORA_ACTIONS.EVENTO_DEPOSITO_APLICADO,
        {
          monto: hayDevolucion ? r.montoDevuelto : d.monto,
          montoAplicado: r.montoAplicado,
          montoDevuelto: r.montoDevuelto || 0,
        }
      );
      // Si Firestore rechazó el cambio, el toast ya lo dijo: el diálogo se
      // queda abierto con lo capturado y NO se cuelga ningún comprobante a un
      // depósito que sigue en custodia.
      if (!ok) return;
      if (hayDevolucion && r.montoAplicado > 0) {
        // Parcial: la bitácora cuenta las dos caras.
        logAction(BITACORA_ACTIONS.EVENTO_DEPOSITO_APLICADO, d.id, {
          monto: r.montoAplicado,
          parcial: true,
        });
      }
      if (res.archivo && hayDevolucion) {
        try {
          const archivoRef = doc(
            collection(db, "venues", venueId, "calendars", "default", "events", eventId, "archivos")
          );
          await setDoc(archivoRef, {
            label: `Comprobante de devolución ${fmtDate(fechaTs)}`.trim(),
            tipo: "comprobante",
            storagePath: res.archivo.storagePath,
            filename: res.archivo.filename,
            fileSize: res.archivo.fileSize,
            mimeType: res.archivo.mimeType,
            uploadedAt: Timestamp.now(),
            uploadedBy: user?.uid || null,
            uploadedByName: userInfo?.name || "",
            linkedTo: { kind: "deposito", id: d.id },
            linkedRole: "devolucion",
          });
          reloadArchivos();
        } catch (err) {
          console.error("Error saving devolucion comprobante:", err);
          showAlert("Devolución registrada, pero falló al adjuntar el comprobante.", "warning");
          try { await deleteObject(storageRef(storage, res.archivo.storagePath)); } catch (_) {}
        }
      }
      setResolviendo(null);
    } finally {
      setSavingResolver(false);
    }
  };

  const handleReactivar = async () => {
    const d = reactivando;
    if (!d) return;
    await handlePatch(
      d.id,
      { estado: "activo", montoDevuelto: null, fechaDevolucion: null },
      BITACORA_ACTIONS.EVENTO_DEPOSITO_REACTIVAR,
      { monto: d.monto, estadoAnterior: d.estado }
    );
    setReactivando(null);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    try {
      const linkedSet = archivosByDeposito[deleteCandidate.id] || {};
      const linkedArchivos = [linkedSet.pago, linkedSet.devolucion].filter(Boolean);
      await deleteDoc(doc(db, "venues", venueId, "clienteDepositos", deleteCandidate.id));
      for (const linkedArchivo of linkedArchivos) {
        if (linkedArchivo.storagePath) {
          try {
            await deleteObject(storageRef(storage, linkedArchivo.storagePath));
          } catch (err) {
            if (err?.code !== "storage/object-not-found") {
              console.error("Error deleting linked archivo blob:", err);
            }
          }
        }
        try {
          await deleteDoc(doc(db, "venues", venueId, "calendars", "default", "events", eventId, "archivos", linkedArchivo.id));
        } catch (err) {
          console.error("Error deleting linked archivo doc:", err);
        }
      }
      logAction(BITACORA_ACTIONS.EVENTO_DEPOSITO_DELETE, deleteCandidate.id, {
        monto: deleteCandidate.monto,
      });
      setDeleteCandidate(null);
      reloadArchivos();
      showAlert("Depósito eliminado", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  return (
    <div className="space-y-4">
      {/* Hero: en custodia */}
      <div
        className="rounded-xl p-4 text-white shadow-sm"
        style={{ backgroundColor: ACCENT }}
      >
        <div className="flex items-center gap-2 mb-1">
          <BanknotesIcon className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wide opacity-90">
            En custodia
          </span>
        </div>
        <div className="text-2xl font-bold">{fmtMoney(totals.activo)}</div>
        <div className="text-xs opacity-90 mt-1">
          Aún retenido por el venue
        </div>
      </div>
      {/* Devueltos + Aplicados */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-3 shadow-sm">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Devueltos
          </div>
          <div className="text-xl font-bold text-emerald-600 mt-0.5">
            {fmtMoney(totals.devuelto)}
          </div>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-3 shadow-sm">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Aplicados
          </div>
          <div className="text-xl font-bold text-red-600 mt-0.5">
            {fmtMoney(totals.aplicado)}
          </div>
        </div>
      </div>

      {/* Actions header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BanknotesIcon className="h-5 w-5" style={{ color: ACCENT }} />
          <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
            Depósitos en garantía del cliente
          </h3>
        </div>
        {!showAdd && !soloLectura && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            Registrar depósito
          </button>
        )}
      </div>

      {showAdd && (
        <DepositoForm
          mode="create"
          venueId={venueId}
          eventId={eventId}
          onCancel={() => setShowAdd(false)}
          onSubmit={handleCreate}
        />
      )}

      {/* List */}
      {loading ? (
        <div className="py-8 text-center text-zinc-400 text-sm">Cargando...</div>
      ) : depositos.length === 0 && !showAdd ? (
        <div className="py-8 text-center text-zinc-400 text-sm italic">
          Aún no hay depósitos registrados para este evento.
        </div>
      ) : (
        <div className="space-y-2">
          {depositos.map((d) => {
            const badge = ESTADO_BADGE[d.estado] || ESTADO_BADGE.activo;
            if (editingId === d.id) {
              return (
                <DepositoForm
                  key={d.id}
                  mode="edit"
                  initial={d}
                  onCancel={() => setEditingId(null)}
                  onSubmit={async (patch) => {
                    await handlePatch(
                      d.id,
                      {
                        monto: patch.monto,
                        fecha: patch.fecha,
                        metodo: patch.metodo,
                        notas: patch.notas,
                      },
                      BITACORA_ACTIONS.EVENTO_DEPOSITO_EDIT,
                      { monto: patch.monto }
                    );
                    setEditingId(null);
                  }}
                />
              );
            }
            return (
              <div key={d.id} className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-3">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-bold text-zinc-900">{fmtMoney(d.monto)}</span>
                      <Badge color={badge.color}>{badge.label}</Badge>
                    </div>
                    {/* Breakdown chips: cuánto se devolvió y cuánto se quedó el venue */}
                    {(d.estado === "devuelto" || d.estado === "aplicado") && (() => {
                      const devuelto = Number(d.montoDevuelto) || 0;
                      const aplicado = Math.max(0, Number(d.monto) - devuelto);
                      return (
                        <div className="mt-1.5 flex gap-2 flex-wrap text-xs">
                          {devuelto > 0 && (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-semibold">
                              Devuelto: {fmtMoney(devuelto)}
                            </span>
                          )}
                          {aplicado > 0 && (
                            <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-semibold">
                              Aplicado: {fmtMoney(aplicado)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <div className="text-xs text-zinc-500 mt-1">
                      {fmtDate(d.fecha)}
                      {d.metodo && <> · {d.metodo}</>}
                    </div>
                    {d.notas && (
                      <div className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap">{d.notas}</div>
                    )}
                    {d.estado === "devuelto" && (
                      <div className="text-xs text-emerald-700 mt-2">
                        Devuelto el {fmtDate(d.fechaDevolucion)}
                        {d.metodoDevolucion && <> · {d.metodoDevolucion}</>}
                        {d.notasDevolucion && (
                          <span className="text-zinc-500"> — {d.notasDevolucion}</span>
                        )}
                      </div>
                    )}
                    {/* El motivo por el que el venue se lo quedó se captura al
                        resolver; aquí es donde se lee después. */}
                    {d.estado === "aplicado" && (
                      <div className="text-xs text-red-700 mt-2">
                        Aplicado el {fmtDate(d.fechaDevolucion)}
                        {d.notasDevolucion && (
                          <span className="text-zinc-500"> — {d.notasDevolucion}</span>
                        )}
                      </div>
                    )}
                    <ComprobanteFooter
                      venueId={venueId}
                      eventId={eventId}
                      eventNombre={eventNombre}
                      depositoId={d.id}
                      fechaPago={d.fecha}
                      fechaDevolucion={d.fechaDevolucion}
                      estado={d.estado}
                      pagoArchivo={archivosByDeposito[d.id]?.pago}
                      devolucionArchivo={archivosByDeposito[d.id]?.devolucion}
                      currentUser={user}
                      userInfo={userInfo}
                      onChanged={reloadArchivos}
                      soloLectura={soloLectura}
                    />
                  </div>
                  {!soloLectura && (
                  <div className="flex gap-1 flex-wrap">
                    {d.estado === "activo" ? (
                      <button
                        type="button"
                        onClick={() => setResolviendo(d)}
                        className="text-xs px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        title="Resolver: aplicar, devolver o repartir el depósito"
                      >
                        Resolver
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setReactivando(d)}
                        className="text-xs px-2 py-1 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200"
                        title="Reactivar (vuelve a 'En custodia')"
                      >
                        Reactivar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditingId(d.id)}
                      className="text-zinc-400 hover:text-zinc-600 p-1.5"
                      title="Editar"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteCandidate(d)}
                      className="text-zinc-400 hover:text-red-500 p-1.5"
                      title="Eliminar"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ResolverDepositoDialog
        open={!!resolviendo}
        onClose={() => setResolviendo(null)}
        deposito={resolviendo}
        fuente="cliente"
        storagePathPrefix={venueId && eventId ? `venues/${venueId}/eventos/${eventId}/archivos` : null}
        saving={savingResolver}
        onSubmit={handleResolver}
      />

      <DialogoConfirmacion
        open={!!reactivando}
        onClose={() => setReactivando(null)}
        titulo="Reactivar depósito"
        subtitulo={`${fmtMoney(reactivando?.monto)} · hoy marcado como ${
          reactivando?.estado === "devuelto" ? "devuelto" : "aplicado"
        }`}
        icono={BanknotesIcon}
        confirmar={{ label: "Sí, reactivar", onClick: handleReactivar }}
      >
        <p>
          Vuelve a <strong>En custodia</strong>: se borra la devolución o la
          aplicación registrada y el depósito deja de contar como ingreso o
          como devuelto en Cuentas hasta que lo resuelvas de nuevo.
        </p>
      </DialogoConfirmacion>

      <DialogoConfirmacion
        open={!!deleteCandidate}
        onClose={() => setDeleteCandidate(null)}
        titulo="Eliminar depósito"
        subtitulo={fmtMoney(deleteCandidate?.monto)}
        icono={TrashIcon}
        confirmar={{ label: "Eliminar", tono: "rojo", onClick: handleDelete }}
      >
        <p>
          Se borra el registro y sus comprobantes. Esta acción no se puede
          deshacer.
        </p>
      </DialogoConfirmacion>
    </div>
  );
};

export default EventClienteDepositos;
